import Color from 'color';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';

import { HeadlineLink } from 'hb-builder/components/BuilderComponents/block/Strips/components/HeadlineLink';
import type { TemplateWithButtonFrame } from 'hb-builder/components/BuilderComponents/block/Strips/types/items';
import { Children } from 'hb-builder/components/common/Item';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import { ChildrenRenderer } from 'hb-builder/components/Renderer/ChildrenRenderer';
import {
  useComponentContext,
  useBuilderConfigContext,
  useRendererContext,
} from 'hb-builder/hooks';
import type { BuilderComponent } from 'hb-builder/types';

import DumbSquaredImage from './DumbSquaredImage';

const TemplateWithButtonStripsItem: BuilderComponent = () => <div />; // never used

const RenderBuilder = () => {
  const {
    hivebrite: { subdomainNetworkUrl },
  } = useBuilderConfigContext();
  const { item } = useComponentContext();
  const [image, headline, subline, button] =
    item.children as TemplateWithButtonFrame['children'];

  const { attributes: buttonAttributes } = button;
  const { attributes: buttonImage } = image;

  const { buttonLink: link } = buttonAttributes || {};
  const { hidden: imageHidden, clickable } = buttonImage || {};

  const canClickOnHeadline = !!(link && clickable);

  const isFrameBackgroundColorTransparent =
    !item.attributes?.frameBackgroundColor ||
    Color(item.attributes?.frameBackgroundColor).alpha() === 0;

  return (
    <CardWrapper dataTestId="template-with-button-strips-wrapper">
      {!imageHidden && (
        <DumbSquaredImage
          clickable={canClickOnHeadline}
          hasFrameBackgroundColor={!isFrameBackgroundColorTransparent}
          isBackoffice
          image={image}
          subdomainNetworkUrl={subdomainNetworkUrl}
        />
      )}
      <ContentWrapper
        dataTestId="template-with-button-strips-content-wrapper"
        $imageHidden={imageHidden}
        $frameBackgroundColor={
          !isFrameBackgroundColorTransparent &&
          item.attributes?.frameBackgroundColor
        }
      >
        <TextsWrapper>
          {canClickOnHeadline ? (
            <HeadlineLink isBackoffice>
              <Children item={{ children: [headline] }} />
            </HeadlineLink>
          ) : (
            <Box>
              <Children item={{ children: [headline] }} />
            </Box>
          )}
          <Children item={{ children: [subline] }} />
        </TextsWrapper>
        <Children item={{ children: [button] }} />
      </ContentWrapper>
    </CardWrapper>
  );
};

const Render = ({ item }: RenderProps<TemplateWithButtonFrame>) => {
  const { subdomainNetworkUrl } = useRendererContext();
  const [image, headline, subline, button] = item?.children || [];

  const { clickable, hidden: imageHidden } = image?.attributes || {};

  const { buttonLink: link, buttonLinkNewPage: linkNewPage } =
    button?.attributes || {};

  const isFrameBackgroundColorTransparent =
    !item?.attributes?.frameBackgroundColor ||
    Color(item?.attributes?.frameBackgroundColor).alpha() === 0;

  const props = {
    clickable,
    hasFrameBackgroundColor: !isFrameBackgroundColorTransparent,
    fallBackAltValue: headline?.content,
    link,
    linkNewPage,
    image,
    subdomainNetworkUrl,
  };

  const canClickOnHeadline = link && clickable;

  return (
    <CardWrapper dataTestId="template-with-button-strips-wrapper">
      {!imageHidden && <DumbSquaredImage {...props} />}
      <ContentWrapper
        dataTestId="template-with-button-strips-content-wrapper"
        $imageHidden={imageHidden}
        $frameBackgroundColor={
          !isFrameBackgroundColorTransparent &&
          item?.attributes?.frameBackgroundColor
        }
      >
        <TextsWrapper>
          {canClickOnHeadline ? (
            <HeadlineLink href={link} target={linkNewPage ? '_blank' : '_self'}>
              <ChildrenRenderer item={{ children: [headline] }} />
            </HeadlineLink>
          ) : (
            <Box>
              <ChildrenRenderer item={{ children: [headline] }} />
            </Box>
          )}
          <ChildrenRenderer item={{ children: [subline] }} />
        </TextsWrapper>
        <ChildrenRenderer item={{ children: [button] }} />
      </ContentWrapper>
    </CardWrapper>
  );
};

TemplateWithButtonStripsItem.item = ({ children, template }) => ({
  attributes: {},
  children,
  template,
  type: TemplateWithButtonStripsItem.componentName,
});

TemplateWithButtonStripsItem.componentName = 'TemplateWithButtonStripsItem';
TemplateWithButtonStripsItem.componentType = 'single';

TemplateWithButtonStripsItem.canBeDoubleClicked = false;
TemplateWithButtonStripsItem.canBeDragged = false;
TemplateWithButtonStripsItem.canBeListed = false;
TemplateWithButtonStripsItem.canBeSelected = false;
TemplateWithButtonStripsItem.componentClickable = false;

TemplateWithButtonStripsItem.RenderBuilder = RenderBuilder;
TemplateWithButtonStripsItem.Render = Render;

export default TemplateWithButtonStripsItem;

const PB_IF_FRAME_BACKGROUND = 6;
const PT_IF_FRAME_BACKGROUND_AND_IMAGE = 6;

const CardWrapper = styled(Box).attrs({
  flexDirection: 'column',
  height: '100%',
  width: '100%',
})``;

interface ContentWrapperProps {
  $frameBackgroundColor?: string;
  $imageHidden?: boolean;
}

const ContentWrapper = styled(Box).attrs(
  ({ $frameBackgroundColor, $imageHidden }: ContentWrapperProps) => ({
    ...($frameBackgroundColor && {
      bg: $frameBackgroundColor,
      pb: PB_IF_FRAME_BACKGROUND,
      px: 6,
    }),
    flexDirection: 'column',
    justifyContent: 'space-between',
    pt:
      !$frameBackgroundColor && $imageHidden
        ? 0
        : PT_IF_FRAME_BACKGROUND_AND_IMAGE,
    width: '100%',
  }),
)<ContentWrapperProps>`
  ${({ $frameBackgroundColor, $imageHidden, theme }) => {
    const ptBgAndImg = $imageHidden ? 0 : PT_IF_FRAME_BACKGROUND_AND_IMAGE;
    const verticalPadding = !$frameBackgroundColor
      ? 0
      : PB_IF_FRAME_BACKGROUND + ptBgAndImg;

    return css`
      height: calc(100% - ${verticalPadding * 4}px);

      ${$frameBackgroundColor &&
      css`
        border-radius: ${$imageHidden
            ? `${theme.themeVariables.globalBorderRadius}px`
            : '0px'}
          ${$imageHidden
            ? `${theme.themeVariables.globalBorderRadius}px`
            : '0px'}
          ${theme.themeVariables.globalBorderRadius}px
          ${theme.themeVariables.globalBorderRadius}px;
      `}
    `;
  }}
`;

const TextsWrapper = styled(Box).attrs({
  flexDirection: 'column',
})``;
